import React, { useState, useEffect } from 'react';
import { Heart, Star, Home, ShoppingCart, Trophy, Info } from 'lucide-react';

const API_BASE = '/vrihi/api';

// Seed types with colors and emojis
const SEED_TYPES = [
  { id: 'wheat', name: 'Wheat', emoji: '🌾', color: '#FFD700', points: 10 },
  { id: 'rice', name: 'Rice', emoji: '🍚', color: '#F5F5DC', points: 10 },
  { id: 'mustard', name: 'Mustard', emoji: '🟡', color: '#FFDB58', points: 15 },
  { id: 'cotton', name: 'Cotton', emoji: '☁️', color: '#FFFFFF', points: 15 },
  { id: 'chickpea', name: 'Chickpea', emoji: '🫘', color: '#D2691E', points: 20 },
  { id: 'millet', name: 'Millet', emoji: '🌰', color: '#8B7355', points: 20 },
  { id: 'sunflower', name: 'Sunflower', emoji: '🌻', color: '#FFD54F', points: 25 },
];

interface UserData {
  id: string;
  username: string;
  email: string;
  wallet: {
    ahn: number;
    urja: number;
  };
}

interface Progress {
  lives_remaining: number;
  current_level: number;
  highest_level_unlocked: number;
  total_stars: number;
  total_score: number;
}

interface Level {
  level_number: number;
  level_type: string;
  objective_data: any;
  moves_allowed: number;
  star_1_threshold: number;
  star_2_threshold: number;
  star_3_threshold: number;
  user_score: number;
  user_stars: number;
  completed: boolean;
}

type ViewMode = 'loading' | 'menu' | 'levels' | 'play' | 'shop';

function App() {
  const [user, setUser] = useState<UserData | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('loading');

  // Game state
  const [levels, setLevels] = useState<Level[]>([]);
  const [currentLevel, setCurrentLevel] = useState<Level | null>(null);
  const [board, setBoard] = useState<string[][]>([]);
  const [score, setScore] = useState(0);
  const [movesLeft, setMovesLeft] = useState(0);
  const [selectedCell, setSelectedCell] = useState<{row: number, col: number} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async (targetView?: ViewMode) => {
    try {
      if (!token) {
        setError('Please login via main Ahantravyuh app');
        setLoading(false);
        return;
      }

      // Load user data
      const userRes = await fetch(`${API_BASE}/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userData = await userRes.json();
      setUser(userData);

      // Load progress
      const progressRes = await fetch(`${API_BASE}/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const progressData = await progressRes.json();
      setProgress(progressData);

      // Load levels
      const levelsRes = await fetch(`${API_BASE}/levels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const levelsData = await levelsRes.json();
      setLevels(levelsData.levels);

      setLoading(false);
      if (targetView) {
        setViewMode(targetView);
      } else if (viewMode === 'loading') {
        setViewMode('menu');
      }
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const startLevel = async (level: Level) => {
    try {
      if (!progress || progress.lives_remaining <= 0) {
        alert('No lives remaining! Buy more from the shop.');
        return;
      }

      // Start level (consumes 1 life)
      await fetch(`${API_BASE}/levels/${level.level_number}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Initialize game board
      const newBoard = generateBoard();
      setBoard(newBoard);
      setCurrentLevel(level);
      setScore(0);
      setMovesLeft(level.moves_allowed);
      setSelectedCell(null);
      setViewMode('play');

      // Update lives
      if (progress) {
        setProgress({ ...progress, lives_remaining: progress.lives_remaining - 1 });
      }
    } catch (err) {
      alert('Failed to start level');
    }
  };

  const generateBoard = (): string[][] => {
    const size = 8;
    const board: string[][] = [];

    for (let row = 0; row < size; row++) {
      const newRow: string[] = [];
      for (let col = 0; col < size; col++) {
        // Use first 5 seed types for easier gameplay
        const randomSeed = SEED_TYPES[Math.floor(Math.random() * 5)];
        newRow.push(randomSeed.id);
      }
      board.push(newRow);
    }

    return board;
  };

  const handleCellClick = (row: number, col: number) => {
    if (isProcessing || movesLeft <= 0) return;

    if (!selectedCell) {
      // Select first cell
      setSelectedCell({ row, col });
    } else {
      // Check if adjacent
      const rowDiff = Math.abs(selectedCell.row - row);
      const colDiff = Math.abs(selectedCell.col - col);

      if ((rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1)) {
        // Adjacent cell - swap
        swapSeeds(selectedCell.row, selectedCell.col, row, col);
      }

      setSelectedCell(null);
    }
  };

  const swapSeeds = async (row1: number, col1: number, row2: number, col2: number) => {
    setIsProcessing(true);

    // Swap
    const newBoard = board.map(r => [...r]);
    [newBoard[row1][col1], newBoard[row2][col2]] = [newBoard[row2][col2], newBoard[row1][col1]];
    setBoard(newBoard);

    // Check for matches
    await new Promise(resolve => setTimeout(resolve, 300));
    const matchFound = await processMatches(newBoard);

    if (!matchFound) {
      // No match - swap back
      [newBoard[row1][col1], newBoard[row2][col2]] = [newBoard[row2][col2], newBoard[row1][col1]];
      setBoard(newBoard);
    } else {
      // Valid move
      setMovesLeft(prev => prev - 1);
    }

    setIsProcessing(false);
  };

  const processMatches = async (currentBoard: string[][]): Promise<boolean> => {
    let matchFound = false;
    let boardCopy = currentBoard.map(r => [...r]);

    // Find matches
    const toRemove: Set<string> = new Set();

    // Check horizontal matches
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 6; col++) {
        const seed = boardCopy[row][col];
        if (seed && boardCopy[row][col + 1] === seed && boardCopy[row][col + 2] === seed) {
          toRemove.add(`${row},${col}`);
          toRemove.add(`${row},${col + 1}`);
          toRemove.add(`${row},${col + 2}`);
          matchFound = true;
        }
      }
    }

    // Check vertical matches
    for (let col = 0; col < 8; col++) {
      for (let row = 0; row < 6; row++) {
        const seed = boardCopy[row][col];
        if (seed && boardCopy[row + 1][col] === seed && boardCopy[row + 2][col] === seed) {
          toRemove.add(`${row},${col}`);
          toRemove.add(`${row + 1},${col}`);
          toRemove.add(`${row + 2},${col}`);
          matchFound = true;
        }
      }
    }

    if (matchFound) {
      // Calculate score
      let pointsEarned = 0;
      toRemove.forEach(key => {
        const [row, col] = key.split(',').map(Number);
        const seedId = boardCopy[row][col];
        const seedType = SEED_TYPES.find(s => s.id === seedId);
        if (seedType) {
          pointsEarned += seedType.points;
        }
      });
      setScore(prev => prev + pointsEarned);

      // Remove matched seeds
      toRemove.forEach(key => {
        const [row, col] = key.split(',').map(Number);
        boardCopy[row][col] = '';
      });
      setBoard(boardCopy);

      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 300));

      // Drop seeds
      boardCopy = dropSeeds(boardCopy);
      setBoard(boardCopy);

      await new Promise(resolve => setTimeout(resolve, 300));

      // Fill empty spaces
      boardCopy = fillBoard(boardCopy);
      setBoard(boardCopy);

      await new Promise(resolve => setTimeout(resolve, 300));

      // Check for cascading matches
      await processMatches(boardCopy);
    }

    return matchFound;
  };

  const dropSeeds = (currentBoard: string[][]): string[][] => {
    const newBoard = currentBoard.map(r => [...r]);

    for (let col = 0; col < 8; col++) {
      let emptySpaces = 0;
      for (let row = 7; row >= 0; row--) {
        if (newBoard[row][col] === '') {
          emptySpaces++;
        } else if (emptySpaces > 0) {
          newBoard[row + emptySpaces][col] = newBoard[row][col];
          newBoard[row][col] = '';
        }
      }
    }

    return newBoard;
  };

  const fillBoard = (currentBoard: string[][]): string[][] => {
    const newBoard = currentBoard.map(r => [...r]);

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (newBoard[row][col] === '') {
          const randomSeed = SEED_TYPES[Math.floor(Math.random() * 5)];
          newBoard[row][col] = randomSeed.id;
        }
      }
    }

    return newBoard;
  };

  const endLevel = async () => {
    if (!currentLevel) return;

    try {
      const response = await fetch(`${API_BASE}/levels/${currentLevel.level_number}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          score,
          movesUsed: currentLevel.moves_allowed - movesLeft,
          powerUpsUsed: {}
        })
      });

      const result = await response.json();

      // Show result
      alert(result.message);

      // Reload data and go back to levels
      await loadInitialData('levels');
    } catch (err) {
      alert('Failed to submit score');
    }
  };

  useEffect(() => {
    if (viewMode === 'play' && movesLeft === 0) {
      endLevel();
    }
  }, [movesLeft]);

  // Check if objective is met (winning condition)
  useEffect(() => {
    if (viewMode === 'play' && currentLevel && !isProcessing) {
      if (currentLevel.level_type === 'score' && score >= currentLevel.objective_data.target) {
        // Player reached the score target - end level successfully!
        endLevel();
      }
    }
  }, [score, isProcessing]);

  const buyLife = async () => {
    try {
      const response = await fetch(`${API_BASE}/shop/buy-life`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        alert(`Purchased 1 life for ${result.cost} AHN`);
        await loadInitialData();
      } else {
        alert(result.error);
      }
    } catch (err) {
      alert('Failed to buy life');
    }
  };

  if (loading || viewMode === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 flex items-center justify-center">
        <div className="text-2xl">🌱 Loading VRIHI...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-red-800 text-xl font-bold mb-4">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Main Menu
  if (viewMode === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 to-yellow-400 p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 mt-8">
            <h1 className="text-5xl font-bold text-white mb-2">🌱 VRIHI</h1>
            <p className="text-white text-lg">वृहि - Match Seeds & Harvest Rewards</p>
          </div>

          {/* User Info */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-bold">{user?.username}</p>
                <p className="text-sm text-gray-600">{user?.wallet.ahn.toFixed(0)} AHN</p>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="text-red-500" size={24} />
                <span className="text-xl font-bold">{progress?.lives_remaining || 0}/5</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Star className="text-yellow-500" size={20} />
              <span className="font-semibold">{progress?.total_stars || 0} stars</span>
              <Trophy className="text-orange-500 ml-4" size={20} />
              <span className="font-semibold">{progress?.total_score || 0} pts</span>
            </div>
          </div>

          {/* Menu Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => loadInitialData('levels')}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-lg font-bold text-lg shadow-lg"
            >
              🎮 Play
            </button>

            <button
              onClick={() => setViewMode('shop')}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white py-4 rounded-lg font-bold text-lg shadow-lg"
            >
              <ShoppingCart className="inline mr-2" size={24} />
              Shop
            </button>

            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold shadow-lg"
            >
              <Home className="inline mr-2" size={20} />
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Level Selection
  if (viewMode === 'levels') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 to-yellow-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setViewMode('menu')}
              className="bg-white px-4 py-2 rounded-lg shadow"
            >
              ← Back
            </button>
            <div className="flex items-center gap-4 bg-white px-4 py-2 rounded-lg shadow">
              <div className="flex items-center gap-1">
                <Heart className="text-red-500" size={20} />
                <span className="font-bold">{progress?.lives_remaining || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="text-yellow-500" size={20} />
                <span className="font-bold">{progress?.total_stars || 0}</span>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mb-6 text-center">Select Level</h2>

          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {levels.map(level => (
              <button
                key={level.level_number}
                onClick={() => startLevel(level)}
                disabled={level.level_number > (progress?.highest_level_unlocked || 1)}
                className={`aspect-square rounded-lg shadow-lg font-bold text-lg
                  ${level.level_number > (progress?.highest_level_unlocked || 1)
                    ? 'bg-gray-300 cursor-not-allowed'
                    : level.completed
                    ? 'bg-green-400 hover:bg-green-500'
                    : 'bg-blue-400 hover:bg-blue-500'
                  } text-white transition-all`}
              >
                <div>{level.level_number}</div>
                {level.completed && (
                  <div className="flex justify-center mt-1">
                    {Array(level.user_stars).fill(0).map((_, i) => (
                      <Star key={i} size={12} fill="gold" color="gold" />
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Game Play
  if (viewMode === 'play' && currentLevel) {
    const getSeedEmoji = (seedId: string) => {
      return SEED_TYPES.find(s => s.id === seedId)?.emoji || '?';
    };

    const getStarsForScore = () => {
      if (score >= currentLevel.star_3_threshold) return 3;
      if (score >= currentLevel.star_2_threshold) return 2;
      if (score >= currentLevel.star_1_threshold) return 1;
      return 0;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-200 to-yellow-200 p-2 md:p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg p-3 mb-3 shadow-lg">
            <div className="flex justify-between items-center">
              <div className="text-lg font-bold">Level {currentLevel.level_number}</div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-xl">{movesLeft}</span>
                  <span className="text-sm">moves</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-bold text-xl text-green-600">{score}</span>
                  <span className="text-sm">pts</span>
                </div>
              </div>
            </div>

            {/* Stars Progress */}
            <div className="mt-2 flex items-center gap-2">
              {[1, 2, 3].map(starNum => (
                <Star
                  key={starNum}
                  size={24}
                  fill={getStarsForScore() >= starNum ? 'gold' : 'transparent'}
                  color={getStarsForScore() >= starNum ? 'gold' : 'gray'}
                />
              ))}
              <span className="text-sm ml-2">Target: {currentLevel.star_1_threshold}</span>
            </div>
          </div>

          {/* Game Board */}
          <div className="bg-white rounded-lg p-2 md:p-4 shadow-lg">
            <div className="grid grid-cols-8 gap-1" style={{ maxWidth: '100%' }}>
              {board.map((row, rowIdx) =>
                row.map((seedId, colIdx) => (
                  <div
                    key={`${rowIdx}-${colIdx}`}
                    onClick={() => handleCellClick(rowIdx, colIdx)}
                    className={`
                      aspect-square flex items-center justify-center text-2xl md:text-3xl
                      rounded-lg cursor-pointer transition-all
                      ${selectedCell?.row === rowIdx && selectedCell?.col === colIdx
                        ? 'bg-blue-200 scale-110'
                        : seedId
                        ? 'bg-gradient-to-br from-green-50 to-yellow-50 hover:scale-105'
                        : 'bg-gray-200'
                      }
                      ${isProcessing ? 'pointer-events-none' : ''}
                    `}
                  >
                    {seedId && getSeedEmoji(seedId)}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Objective */}
          <div className="bg-white rounded-lg p-3 mt-3 shadow-lg text-center">
            <p className="font-semibold">
              {currentLevel.level_type === 'score' && `Reach ${currentLevel.objective_data.target} points`}
              {currentLevel.level_type === 'collection' && 'Collect specific seeds'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Shop
  if (viewMode === 'shop') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-4">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => setViewMode('menu')}
            className="mb-6 px-4 py-2 bg-white rounded-lg shadow"
          >
            ← Back
          </button>

          <h2 className="text-3xl font-bold mb-6 text-center">Shop</h2>

          <div className="bg-white rounded-lg p-4 mb-4 shadow-lg">
            <p className="text-center mb-2">Your AHN Balance</p>
            <p className="text-4xl font-bold text-center text-green-600">
              {user?.wallet.ahn.toFixed(0)}
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-bold">1 Life</h3>
                  <p className="text-gray-600">Get back in the game</p>
                </div>
                <Heart className="text-red-500" size={48} />
              </div>
              <button
                onClick={buyLife}
                disabled={!user || user.wallet.ahn < 2 || (progress?.lives_remaining || 0) >= 5}
                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white py-3 rounded-lg font-bold"
              >
                Buy for 2 AHN
              </button>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-bold">5 Lives (Full Refill)</h3>
                  <p className="text-gray-600">Best value!</p>
                </div>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Heart key={i} className="text-red-500" size={20} />
                  ))}
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_BASE}/shop/refill-lives`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const result = await response.json();
                    if (result.success) {
                      alert(`Lives refilled for ${result.cost} AHN!`);
                      await loadInitialData();
                    }
                  } catch (err) {
                    alert('Failed to refill lives');
                  }
                }}
                disabled={!user || user.wallet.ahn < 8}
                className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white py-3 rounded-lg font-bold"
              >
                Buy for 8 AHN
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
